<template>
  <div class="login-form">
    <header class="login-form__header">
      <h2>Iniciar sesión</h2>
      <p>Bienvenido de nuevo, ingresa tus credenciales para continuar.</p>
    </header>

    <!-- Error de autenticación (credenciales inválidas, cuenta inactiva, etc.) -->
    <div
      v-if="loginError"
      class="login-alert"
      :class="{ shake: shaking }"
      role="alert"
      aria-live="assertive"
    >
      <i class="pi pi-exclamation-circle" aria-hidden="true" />
      <span>{{ loginError }}</span>
    </div>

    <form novalidate @submit.prevent="handleSubmit">
      <div class="field">
        <label for="email">Correo electrónico</label>
        <InputText
          id="email"
          v-model="formData.email"
          type="email"
          autocomplete="email"
          placeholder="tu@email.com"
          autofocus
          :invalid="!!errors.email"
          :aria-describedby="errors.email ? 'email-error' : undefined"
          @input="onEmailInput"
        />
        <small v-if="errors.email" id="email-error" class="field-error">
          <i class="pi pi-exclamation-circle" aria-hidden="true" /> {{ errors.email }}
        </small>
      </div>

      <div class="field">
        <div class="field-label-row">
          <label for="password">Contraseña</label>
          <RouterLink to="/forgot-password" class="forgot-link">
            ¿Olvidaste tu contraseña?
          </RouterLink>
        </div>
        <Password
          ref="passwordRef"
          v-model="formData.password"
          input-id="password"
          placeholder="Ingresa tu contraseña"
          :feedback="false"
          toggle-mask
          :input-props="{ autocomplete: 'current-password' }"
          :invalid="!!errors.password"
          :aria-describedby="errors.password ? 'password-error' : undefined"
          @input="onPasswordInput"
        />
        <small v-if="errors.password" id="password-error" class="field-error">
          <i class="pi pi-exclamation-circle" aria-hidden="true" /> {{ errors.password }}
        </small>
        <small v-if="capsLockOn" class="caps-warning">
          <i class="pi pi-arrow-up" aria-hidden="true" /> Bloq Mayús está activado
        </small>
      </div>

      <Button
        type="submit"
        label="Iniciar sesión"
        icon="pi pi-sign-in"
        class="submit-btn"
        :loading="isLoading"
        :disabled="oauthLoading"
      />

      <!-- Separador -->
      <div class="oauth-divider">
        <span>o continúa con</span>
      </div>

      <!-- Botones OAuth -->
      <div class="oauth-buttons">
        <button
          type="button"
          class="oauth-btn google"
          :disabled="oauthLoading || isLoading"
          @click="loginWithGoogle"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google
        </button>

        <button
          type="button"
          class="oauth-btn microsoft"
          :disabled="oauthLoading || isLoading"
          @click="loginWithMicrosoft"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#F25022" d="M1 1h10v10H1z"/>
            <path fill="#00A4EF" d="M13 1h10v10H13z"/>
            <path fill="#7FBA00" d="M1 13h10v10H1z"/>
            <path fill="#FFB900" d="M13 13h10v10H13z"/>
          </svg>
          Microsoft
        </button>
      </div>

      <p class="register-hint">
        ¿Aún no tienes cuenta?
        <RouterLink to="/suscribirse">Registrar nueva empresa</RouterLink>
      </p>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue';
import { useAuthStore } from '../../stores/auth';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import api from '../../services/api';

const emit = defineEmits(['login-success']);

const authStore = useAuthStore();
const toast = useToast();

const formData = reactive({
  email: '',
  password: '',
});

const errors = reactive({
  email: '',
  password: '',
});

const isLoading = ref(false);
const oauthLoading = ref(false);
const loginError = ref('');
const shaking = ref(false);
const capsLockOn = ref(false);
const passwordRef = ref(null);

// --- Detección de Bloq Mayús en el campo de contraseña ---
const handleCapsLock = (event) => {
  capsLockOn.value = event.getModifierState?.('CapsLock') ?? false;
};

let passwordInputEl = null;

onMounted(() => {
  // Password de PrimeVue envuelve el input real; le adjuntamos los listeners nativos
  passwordInputEl = passwordRef.value?.$el?.querySelector('input');
  if (passwordInputEl) {
    passwordInputEl.addEventListener('keydown', handleCapsLock);
    passwordInputEl.addEventListener('keyup', handleCapsLock);
    passwordInputEl.addEventListener('blur', () => (capsLockOn.value = false));
  }
});

onBeforeUnmount(() => {
  if (passwordInputEl) {
    passwordInputEl.removeEventListener('keydown', handleCapsLock);
    passwordInputEl.removeEventListener('keyup', handleCapsLock);
  }
});

// --- Limpieza de errores mientras el usuario escribe ---
const onEmailInput = () => {
  errors.email = '';
  loginError.value = '';
};

const onPasswordInput = () => {
  errors.password = '';
  loginError.value = '';
};

// --- OAuth ---
const loginWithGoogle = async () => {
  oauthLoading.value = true;
  try {
    const res = await api.get('/auth/oauth/google/url');
    window.location.href = res.data.url;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Google OAuth no está configurado', life: 3000 });
    oauthLoading.value = false;
  }
};

const loginWithMicrosoft = async () => {
  oauthLoading.value = true;
  try {
    const res = await api.get('/auth/oauth/microsoft/url');
    window.location.href = res.data.url;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Microsoft OAuth no está configurado', life: 3000 });
    oauthLoading.value = false;
  }
};

// --- Validación y envío ---
const validateForm = () => {
  let isValid = true;
  errors.email = '';
  errors.password = '';

  if (!formData.email) {
    errors.email = 'El correo es obligatorio';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = 'El correo no es válido';
    isValid = false;
  }

  if (!formData.password) {
    errors.password = 'La contraseña es obligatoria';
    isValid = false;
  }

  return isValid;
};

const triggerShake = () => {
  shaking.value = false;
  // Reinicia la animación en el siguiente frame
  requestAnimationFrame(() => {
    shaking.value = true;
    setTimeout(() => (shaking.value = false), 450);
  });
};

const handleSubmit = async () => {
  loginError.value = '';
  if (!validateForm()) return;

  isLoading.value = true;
  try {
    await authStore.login(formData.email, formData.password);
    toast.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'Sesión iniciada correctamente',
      life: 3000,
    });
    emit('login-success');
  } catch (error) {
    console.error('Error en login:', error);
    loginError.value = error.response?.data?.detail || 'Credenciales incorrectas. Verifica tu correo y contraseña.';
    triggerShake();
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.login-form__header {
  text-align: center;
}

.login-form__header h2 {
  margin: 0 0 0.35rem;
  font-size: 1.45rem;
  font-weight: 700;
  color: #1e293b;
}

.login-form__header p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.login-form form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

.field :deep(.p-inputtext) {
  width: 100%;
}

.field-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.forgot-link {
  font-size: 0.78rem;
  color: #6366f1;
  text-decoration: none;
  white-space: nowrap;
}

.forgot-link:hover {
  text-decoration: underline;
}

.field-error {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #dc2626;
  font-size: 0.78rem;
}

.caps-warning {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #b45309;
  font-size: 0.78rem;
}

.login-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: #b91c1c;
  font-size: 0.85rem;
  line-height: 1.4;
}

.login-alert i {
  margin-top: 0.1rem;
  font-size: 1rem;
}

.shake {
  animation: shake 0.4s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-6px); }
  50% { transform: translateX(6px); }
  75% { transform: translateX(-4px); }
}

.submit-btn {
  width: 100%;
  margin-top: 0.25rem;
}

.oauth-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0;
  color: #9ca3af;
  font-size: 0.82rem;
}

.oauth-divider::before,
.oauth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.oauth-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  transition: all 0.15s;
}

.oauth-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.oauth-btn:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.oauth-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.register-hint {
  margin: 0.25rem 0 0;
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
}

.register-hint a {
  color: #0f766e;
  font-weight: 600;
  text-decoration: none;
}

.register-hint a:hover {
  text-decoration: underline;
}

@media (prefers-reduced-motion: reduce) {
  .shake {
    animation: none;
  }
}
</style>
